import { apiClient } from './apiClient';
import type {
  CalibrationMatrixFilters,
  CalibrationMatrixResponse,
  CalibrationMatrixAppraisalWindowsResponse,
} from './types/calibrationMatrix.types';

function buildMatrixQuery(filters: CalibrationMatrixFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.grade !== null) {
    params.append('grade', filters.grade);
  }
  if (filters.analytics !== null) {
    params.append('analytics', String(filters.analytics));
  }
  for (const geo of filters.geos) {
    params.append('geo', geo);
  }
  if (filters.sector !== null && filters.sector !== '') {
    params.append('sector', filters.sector);
  }
  if (filters.talentCommitteeId !== null) {
    params.append('tcId', String(filters.talentCommitteeId));
  }

  return params;
}

function parseFileNameFromContentDisposition(disposition: string, fallback: string): string {
  if (!disposition) {
    return fallback;
  }
  const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i);
  return match ? decodeURIComponent(match[1]) : fallback;
}

export const calibrationMatrixApi = {
  getMatrix: async (filters: CalibrationMatrixFilters): Promise<CalibrationMatrixResponse> => {
    try {
      const params = buildMatrixQuery(filters);
      const res = await apiClient.get(`/calibration-matrix?${params.toString()}`);
      return res.data;
    } catch (error) {
      console.error('Failed to fetch calibration matrix:', error);
      throw error;
    }
  },

  exportToExcel: async (
    filters: CalibrationMatrixFilters
  ): Promise<{ blob: Blob; fileName: string }> => {
    try {
      const params = buildMatrixQuery(filters);
      const res = await apiClient.get(`/calibration-matrix/export?${params.toString()}`, {
        responseType: 'blob',
      });
      const disposition = (res.headers?.['content-disposition'] as string | undefined) ?? '';
      const fileName = parseFileNameFromContentDisposition(
        disposition,
        'calibration-matrix.xlsx'
      );
      return { blob: res.data, fileName };
    } catch (error) {
      console.error('Failed to export calibration matrix:', error);
      throw error;
    }
  },

  getAppraisalWindows: async (
    appraisalId: number
  ): Promise<CalibrationMatrixAppraisalWindowsResponse> => {
    try {
      const res = await apiClient.get(`/appraisals/${appraisalId}/promotion-window`);
      return res.data;
    } catch (error) {
      console.error('Failed to fetch calibration matrix windows:', error);
      throw error;
    }
  },
};
